/**
 * P2-S6 기관 비식별 집계(FS-15-06 · SR-AUTH-03 — 계약 §3.6 · 세션 역할 org_admin · operator · org_key 경계):
 *   GET /api/org/report?period=month|term|all → { org:{key}, tiles:{courses, activityRunRate, uploadRate, modeCompletion},
 *        courses:[{ course:"ctx-14", grade?, students, activitiesRun, activitiesTotal, uploaded, modeCompletion, retryRate,
 *        roomStability:{ localFallbacks } }] }
 *   …&format=csv → text/csv(같은 표)
 * 개인 식별 필드 없음(학생 이름·별명·개별 결과·순위는 정의상 없다) — 셸은 모르는 필드를 버린다.
 * 비율은 0..1(0..100 이 와도 너그럽게 읽는다).
 */
import { useMemo } from "react";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import type { BytesRequester } from "../../../types/api";
import type { Period, OrgReport } from "../types";

export const PERIODS: readonly Period[] = ["month", "term", "all"];

const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const int = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
};
/** 비율 0..1 — 1 보다 크면 % 로 보고 나눈다 */
const rate = (v: unknown) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(1, n > 1 ? n / 100 : n);
};

/** 학년 표기 — 숫자면 'n학년' 문구는 화면이 붙인다(여기서는 문자열 그대로) */
const grade = (v: unknown): string | null =>
  typeof v === "number" && Number.isFinite(v)
    ? String(v)
    : typeof v === "string" && v.trim()
      ? v.trim()
      : null;

export function readOrgReport(v: unknown): OrgReport {
  const o = obj(v);
  const t = obj(o.tiles);
  return {
    orgKey: typeof obj(o.org).key === "string" ? String(obj(o.org).key) : "",
    tiles: {
      courses: int(t.courses),
      activityRunRate: rate(t.activityRunRate),
      uploadRate: rate(t.uploadRate),
      modeCompletion: rate(t.modeCompletion),
    },
    courses: (Array.isArray(o.courses) ? o.courses : [])
      .map(obj)
      .filter((c) => typeof c.course === "string")
      .map((c) => ({
        course: String(c.course),
        grade: grade(c.grade),
        students: int(c.students),
        activitiesRun: int(c.activitiesRun),
        activitiesTotal: int(c.activitiesTotal),
        uploaded: int(c.uploaded),
        modeCompletion: rate(c.modeCompletion),
        retryRate: rate(c.retryRate),
        localFallbacks: int(obj(c.roomStability).localFallbacks),
      })),
  };
}

export interface OrgApi {
  report(period: Period): Promise<OrgReport>;
  /** CSV 내보내기 — 같은 표를 text/csv 로 */
  csv(period: Period): Promise<Blob>;
}

export function createOrgApi(request: BytesRequester): OrgApi {
  const base = (p: Period) => `/api/org/report?period=${encodeURIComponent(p)}`;
  return {
    report: async (p) => readOrgReport(await request<unknown>(base(p))),
    async csv(p) {
      const bytes = await request<Uint8Array>(`${base(p)}&format=csv`, {
        as: "bytes",
        headers: { Accept: "text/csv" },
      });
      return new Blob([bytes as BlobPart], { type: "text/csv;charset=utf-8" });
    },
  };
}

/** 기관 담당자 · 운영자 세션에서만 */
export function useOrgApi(): OrgApi | null {
  const manager = useSessionManager();
  const state = useSession();
  const ok =
    state.status === "ready" &&
    (state.session.role === "org_admin" || state.session.role === "operator");
  return useMemo(() => (ok ? createOrgApi(manager.request) : null), [manager, ok]);
}
